import React from 'react';
import { 
  Bot, 
  BarChart3, 
  Globe, 
  Palette, 
  PenTool, 
  Settings, 
  Zap,
  Brain,
  FileText,
  TrendingUp,
  Image,
  MessageSquare,
  Wrench,
  Clock
} from 'lucide-react';

const ProductFeatures = () => {
  const features = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: '🤖 Latest AI Models Access',
      items: [
        { label: 'GPT-4o', description: 'Enhanced reasoning and multimodal capabilities' },
        { label: 'o3', description: 'Advanced problem-solving and complex reasoning' },
        { label: 'GPT-4.1 & GPT-4.5', description: 'Improved accuracy and performance' },
        { label: 'GPT-4.1 mini', description: 'Fast responses for quick tasks' },
        { label: 'All Future Models', description: 'Automatic access to new releases' },
        { label: 'Value', description: 'Stay ahead with cutting-edge AI technology' }
      ]
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: '📊 Advanced Data Analysis & Code Interpreter',
      items: [
        { label: 'Upload any file', description: 'Excel, CSV, documents - get instant insights' },
        { label: 'Perfect for', description: 'Data analysis, research, automation, problem-solving' },
        { label: 'Benefits', description: 'Hours of manual work eliminated, professional insights' }
      ]
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: '🌐 Real-Time Web Browsing',
      items: [
        { label: 'Access', description: 'Current information, trends, news, and live data' },
        { label: 'Perfect for', description: 'Research projects, market analysis, fact-checking' },
        { label: 'Benefits', description: 'Always up-to-date information, comprehensive research' }
      ]
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: '🎨 DALL-E Integration & Visual AI',
      items: [
        { label: 'Create', description: 'Custom images, diagrams, visual content' },
        { label: 'Perfect for', description: 'Presentations, social media, creative projects' },
        { label: 'Benefits', description: 'Professional visuals without design skills' }
      ]
    },
    {
      icon: <PenTool className="w-8 h-8" />,
      title: '📝 Advanced Writing & Communication',
      items: [
        { label: 'Generate', description: 'Professional content with latest language models' },
        { label: 'Enhanced', description: 'Grammar, style, tone, and context understanding' },
        { label: 'Benefits', description: 'High-quality content creation at scale' }
      ]
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: '🔧 Custom GPTs & Plugin Ecosystem',
      items: [
        { label: 'Access', description: '1000+ specialized AI assistants and tools' },
        { label: 'Create', description: 'Your own custom GPTs for specific needs' },
        { label: 'Benefits', description: 'Tailored AI solutions for your workflow' }
      ]
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: '💡 Enhanced Productivity Features',
      items: [
        { label: 'Priority Access', description: 'Skip queues, 2x faster responses' },
        { label: 'Extended Conversations', description: 'Handle complex, multi-part projects' },
        { label: 'Advanced Memory', description: 'Context retention across sessions' },
        { label: 'Benefits', description: 'Seamless workflow and consistent results' }
      ]
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Everything You Need to Level Up Your Life and Work
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive AI capabilities to enhance your productivity and creativity
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-black hover:shadow-xl transition-all duration-300 group"
            >
              {/* Feature Header */}
              <div className="flex items-center mb-6">
                <div className="text-black mr-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 group-hover:text-black transition-colors">
                  {feature.title}
                </h3>
              </div>

              {/* Feature Items */}
              <ul className="space-y-4">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                    <div className="flex items-start space-x-2">
                      <span className="text-black text-sm mt-1 flex-shrink-0">•</span>
                      <div>
                        <span className="font-semibold text-gray-900">{item.label}:</span>
                        <span className="text-gray-700 ml-1">{item.description}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section - Key Benefits */}
        <div className="mt-20 bg-black text-white rounded-2xl p-12 max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-8">
            Why Choose Our ChatGPT Plus Access?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">65%</div>
              <p className="text-sm opacity-90">Cost savings compared to official pricing</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">99.9%</div>
              <p className="text-sm opacity-90">Service uptime with professional management</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">24/7</div>
              <p className="text-sm opacity-90">Dedicated support and monitoring</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">5 min</div>
              <p className="text-sm opacity-90">Average activation time after purchase</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductFeatures;